import { usePageMeta, Reveal } from '../lib/site';
import { CardHeader, CtaBand } from '../components/Bits';
import Calculator from '../components/Calculator';
import ReportingFrame from '../components/ReportingFrame';

export default function Why() {
  usePageMeta(
    'Why MyGolfLinks | No barter, no parity clauses, no golfer fees',
    'The incumbent booking model runs on barter, price parity clauses, and convenience fees. MyGolfLinks charges a flat published plan and small transaction fees instead.'
  );

  return (
    <main>
      <section style={{ padding: 'clamp(140px, 16vw, 180px) 0 clamp(48px, 6vw, 70px)' }}>
        <div className="wrap">
          <Reveal>
            <p className="mono" style={{ fontSize: 12.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--fairway)', fontWeight: 500, marginBottom: 20 }}>
              Why MyGolfLinks
            </p>
            <h1 className="display" style={{ maxWidth: 860 }}>
              Your booking platform shouldn't be your biggest competitor.
            </h1>
            <p className="lede" style={{ margin: '24px 0 0' }}>
              Three moves built the dominant model in golf bookings. Courses know all three by
              heart, usually from experience.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ── The three moves ── */}
      <section>
        <div className="wrap" style={{ padding: '0 clamp(20px, 4.5vw, 44px) clamp(64px, 8vw, 100px)' }}>
          <Reveal>
            <CardHeader no="01" label="The three moves" />
          </Reveal>
          <Reveal className="ledger">
            <div className="led">
              <span className="led-no">1</span>
              <span className="led-title">Barter</span>
              <span className="led-body">
                Instead of cash, the platform takes your tee times as payment, then resells that
                inventory, often under your own rack rate, in your own market. You pay in the one
                thing you sell.
              </span>
            </div>
            <div className="led">
              <span className="led-no">2</span>
              <span className="led-title">Price parity clauses</span>
              <span className="led-body">
                Contract language that blocks you from offering a better price on your own website
                than the platform shows. Your own front door is not allowed to beat the middleman.
              </span>
            </div>
            <div className="led">
              <span className="led-no">3</span>
              <span className="led-title">Convenience fees</span>
              <span className="led-body">
                Charged to the golfer at checkout. The golfer does not blame the platform for it;
                they blame the course. You absorb the resentment without seeing the money.
              </span>
            </div>
          </Reveal>
          <Reveal d={1} style={{ marginTop: 34 }}>
            <p className="lede">
              MyGolfLinks runs it differently. A flat published subscription. No barter, no parity
              clauses. And one booking fee total: a flat $1.80 when a golfer books through the app,
              shown upfront before they pay. Bookings through your own website carry no MyGolfLinks
              fee at all.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ── Head to head ── */}
      <section className="band-card">
        <div className="wrap" style={{ padding: 'clamp(64px, 8vw, 100px) clamp(20px, 4.5vw, 44px)' }}>
          <Reveal>
            <CardHeader no="02" label="Head to head" />
            <h2 className="h2" style={{ maxWidth: 620 }}>Against the barter marketplace model, line by line</h2>
            <p className="lede" style={{ marginTop: 16 }}>
              The big booking marketplaces built the playbook most courses live under. Here is the
              whole comparison in one table. No asterisks, and no names needed; you know who runs
              this playbook on your course.
            </p>
          </Reveal>
          <Reveal className="tscroll" style={{ marginTop: 36 }} d={1}>
            <table className="cmp">
              <thead>
                <tr>
                  <th style={{ width: '30%' }}>
                    <span className="mono" style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--mut)' }}>Scorecard</span>
                  </th>
                  <th className="mgl-col">MyGolfLinks</th>
                  <th>The barter marketplace model</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>How the course pays</td>
                  <td className="mgl-col">$197 or $495 a month, published right here</td>
                  <td>Barter: prime tee times taken as payment</td>
                </tr>
                <tr>
                  <td>Your inventory</td>
                  <td className="mgl-col">Never taken, never resold</td>
                  <td>Traded times resold, often under your rack rate</td>
                </tr>
                <tr>
                  <td>Pricing your own course</td>
                  <td className="mgl-col">Price however you want, anywhere</td>
                  <td>Parity clauses can block beating their price on your own site</td>
                </tr>
                <tr>
                  <td>Golfer checkout</td>
                  <td className="mgl-col">A flat $1.80 app booking fee, shown upfront. Zero on your own website</td>
                  <td>Convenience fees at checkout, often $2.49 to $3.49 a round</td>
                </tr>
                <tr>
                  <td>The golfer relationship</td>
                  <td className="mgl-col">Bookings land with the golfer's contact, in your book</td>
                  <td>The golfer belongs to the marketplace</td>
                </tr>
                <tr>
                  <td>Pricing tools and deals</td>
                  <td className="mgl-col">Dynamic pricing and Flash Deals included in the plan</td>
                  <td>Metered, bundled, or negotiated; varies by contract</td>
                </tr>
              </tbody>
            </table>
          </Reveal>
          <Reveal d={2} style={{ marginTop: 44, display: 'grid', justifyItems: 'center' }}>
            <ReportingFrame maxWidth={900} />
          </Reveal>
          <Reveal d={3} style={{ marginTop: 16 }}>
            <p style={{ fontSize: '0.95rem', color: 'var(--mut)', textAlign: 'center', maxWidth: '58ch', margin: '0 auto' }}>
              Straight from the owner portal: what MyGolfLinks took is a labeled tile on your own
              reporting page, not a mystery on a statement.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ── Calculator ── */}
      <section id="calculator">
        <div className="wrap" style={{ padding: 'clamp(64px, 8vw, 100px) clamp(20px, 4.5vw, 44px)' }}>
          <Reveal>
            <CardHeader no="03" label="Your numbers" />
            <h2 className="h2" style={{ maxWidth: 640 }}>Run the math on your own course</h2>
            <p className="lede" style={{ marginTop: 16 }}>
              Pick your current model, set the sliders, and audit the arithmetic. The honest number
              is strong enough that we don't need to inflate it.
            </p>
          </Reveal>
          <Reveal style={{ marginTop: 44 }} d={1}>
            <Calculator />
          </Reveal>
          <Reveal d={2} style={{ marginTop: 26 }}>
            <p style={{ fontSize: '0.88rem', color: 'var(--mut)', maxWidth: '68ch' }}>
              For scale: courses on barter marketplaces commonly give up tens of thousands of
              dollars a year in traded inventory, and high-volume courses give up the most. That is
              exactly why the calculator uses your numbers instead of an industry average.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ── Being early, honestly ── */}
      <section className="band-card">
        <div className="wrap" style={{ padding: 'clamp(56px, 7vw, 84px) clamp(20px, 4.5vw, 44px)' }}>
          <Reveal style={{ maxWidth: 720 }}>
            <CardHeader no="04" label="Where we are" />
            <h2 className="h2">No logo wall. On purpose.</h2>
            <p className="lede" style={{ marginTop: 16 }}>
              MyGolfLinks is early, and we would rather tell you that than fake a testimonial page.
              What we offer instead: founding courses pay $400 once for the platform, forever, get
              direct input on what gets built, and get the kind of attention a platform with ten
              thousand courses cannot give you. If the product doesn't win the demo on its own,
              don't buy it.
            </p>
          </Reveal>
        </div>
      </section>

      <CtaBand
        title="Thirty minutes, your numbers, no pressure"
        sub="We walk the demand side, show the pricing levers live, and leave the calculator open the whole time."
      />
    </main>
  );
}
